/**
 * AdminOnboardingTab - Onboarding step diagnostics.
 * Shows where users drop off before creating value.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  getDashboardOnboarding,
  getDashboardChannels,
  type StepCount,
  type ChannelRow,
} from '@/services/adminDashboardService';

const PERIODS = [
  { value: '7d', label: '7 dias' },
  { value: '14d', label: '14 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
];

const STEP_LABELS: Record<string, string> = {
  account_created: 'Conta criada',
  login_completed: 'Login realizado',
  onboarding_started: 'Onboarding iniciado',
  onboarding_completed: 'Onboarding completo',
  carousel_created: 'Primeiro carrossel',
};

const AdminOnboardingTab: React.FC = () => {
  const [period, setPeriod] = useState('30d');
  const [channel, setChannel] = useState('');
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [steps, setSteps] = useState<StepCount[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch available channels for dropdown
  useEffect(() => {
    getDashboardChannels(period)
      .then((res) => setChannels(res.channels || []))
      .catch(() => {});
  }, [period]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDashboardOnboarding(period, channel || undefined);
      setSteps(data.steps);
    } catch (err) {
      console.error('Failed to fetch onboarding data', err);
    } finally {
      setLoading(false);
    }
  }, [period, channel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <div className="text-muted-foreground py-8 text-center">Carregando dados...</div>;
  }

  const maxVal = Math.max(...steps.map((s) => s.users), 1);

  return (
    <div className="space-y-6">
      {/* Period selector + Channel filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                period === p.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground"
        >
          <option value="">Todos os canais</option>
          {channels.map((ch) => (
            <option key={ch.channel} value={ch.channel}>
              {ch.channel} ({ch.accounts})
            </option>
          ))}
        </select>
      </div>

      {/* Steps */}
      <div className="border border-border rounded-lg p-6">
        <h3 className="text-sm font-semibold text-foreground mb-6">
          Funil de Onboarding
          {channel && <span className="text-muted-foreground font-normal"> — {channel}</span>}
        </h3>

        <div className="space-y-4">
          {steps.map((step, i) => {
            const pct = maxVal > 0 ? (step.users / maxVal) * 100 : 0;
            const prevUsers = i > 0 ? steps[i - 1].users : step.users;
            const dropoff = prevUsers > 0 ? prevUsers - step.users : 0;
            const dropoffPct = prevUsers > 0 ? ((dropoff / prevUsers) * 100).toFixed(1) : '0';
            const convVsTotal = steps[0]?.users > 0
              ? ((step.users / steps[0].users) * 100).toFixed(1)
              : '0';

            return (
              <div key={step.step}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">
                    {STEP_LABELS[step.step] || step.step}
                  </span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-mono text-foreground">{step.users}</span>
                    <span className="text-muted-foreground">{convVsTotal}% do total</span>
                    {i > 0 && dropoff > 0 && (
                      <span className="text-destructive">-{dropoff} ({dropoffPct}%)</span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-4">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(pct, 1)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {steps.length >= 2 && (
          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <h4 className="text-sm font-semibold text-foreground mb-2">Maior gargalo</h4>
            {(() => {
              let maxDrop = 0;
              let maxDropIdx = 0;
              for (let i = 1; i < steps.length; i++) {
                const drop = steps[i - 1].users > 0
                  ? (steps[i - 1].users - steps[i].users) / steps[i - 1].users
                  : 0;
                if (drop > maxDrop) {
                  maxDrop = drop;
                  maxDropIdx = i;
                }
              }
              const fromStep = STEP_LABELS[steps[maxDropIdx - 1]?.step] || steps[maxDropIdx - 1]?.step;
              const toStep = STEP_LABELS[steps[maxDropIdx]?.step] || steps[maxDropIdx]?.step;
              return (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-destructive">{(maxDrop * 100).toFixed(1)}%</span> de drop-off entre{' '}
                  <span className="font-medium text-foreground">{fromStep}</span> e{' '}
                  <span className="font-medium text-foreground">{toStep}</span>
                </p>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOnboardingTab;
